import { createContext, useContext, useEffect } from 'react';
import { SITE, absoluteUrl } from './site';

export type JsonLd = Record<string, unknown>;

export type SeoProps = {
  /** Page topic. Rendered as "<title> | Green Valley Interiors". Aim for 50–60 chars total. */
  title: string;
  /** Unique, 140–160 characters. */
  description: string;
  /** 5–8 long-tail phrases (3–8 words each) describing this page's purpose. */
  keywords: string[];
  /** Canonical path, e.g. "/services/residential-design" (no trailing slash). */
  path: string;
  /** Site-relative or absolute image URL for og:image / twitter:image. */
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  /** Page-specific structured data (BreadcrumbList, Service, Article, FAQPage, …). */
  jsonLd?: JsonLd[];
  article?: { publishedTime: string; modifiedTime?: string; author: string; section: string };
};

/** Server-side collector: the prerender step reads the props of the page's <Seo>. */
export type SeoCollector = { current?: SeoProps };
export const SeoContext = createContext<SeoCollector | null>(null);

export const fullTitle = (title: string) => `${title} | ${SITE.name}`;

const toAbsolute = (url: string) => (/^https?:\/\//.test(url) ? url : `${SITE.origin}${url}`);

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** JSON for a <script type="application/ld+json">, safe against "</script>" injection. */
export const jsonLdString = (data: JsonLd | JsonLd[]) => JSON.stringify(data).replace(/</g, '\\u003c');

type MetaTag = { kind: 'name' | 'property'; key: string; content: string };

function metaTags(p: SeoProps): MetaTag[] {
  const title = fullTitle(p.title);
  const url = absoluteUrl(p.path);
  const image = toAbsolute(p.image ?? SITE.defaultOgImage);
  const tags: MetaTag[] = [
    { kind: 'name', key: 'description', content: p.description },
    { kind: 'name', key: 'keywords', content: p.keywords.join(', ') },
    { kind: 'name', key: 'robots', content: p.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large' },
    { kind: 'property', key: 'og:title', content: title },
    { kind: 'property', key: 'og:description', content: p.description },
    { kind: 'property', key: 'og:url', content: url },
    { kind: 'property', key: 'og:type', content: p.type ?? 'website' },
    { kind: 'property', key: 'og:site_name', content: SITE.name },
    { kind: 'property', key: 'og:locale', content: SITE.locale },
    { kind: 'property', key: 'og:image', content: image },
    { kind: 'name', key: 'twitter:card', content: 'summary_large_image' },
    { kind: 'name', key: 'twitter:title', content: title },
    { kind: 'name', key: 'twitter:description', content: p.description },
    { kind: 'name', key: 'twitter:image', content: image },
  ];
  if (p.article) {
    tags.push(
      { kind: 'property', key: 'article:published_time', content: p.article.publishedTime },
      { kind: 'property', key: 'article:modified_time', content: p.article.modifiedTime ?? p.article.publishedTime },
      { kind: 'property', key: 'article:author', content: p.article.author },
      { kind: 'property', key: 'article:section', content: p.article.section },
    );
  }
  return tags;
}

/** The <head> block written into each prerendered HTML file (between the SEO markers). */
export function renderSeoHead(p: SeoProps): string {
  const lines = [
    `<title>${esc(fullTitle(p.title))}</title>`,
    `<link rel="canonical" href="${esc(absoluteUrl(p.path))}">`,
    ...metaTags(p).map((t) => `<meta ${t.kind}="${t.key}" content="${esc(t.content)}">`),
  ];
  if (p.jsonLd?.length) {
    lines.push(`<script type="application/ld+json" id="page-jsonld">${jsonLdString(p.jsonLd)}</script>`);
  }
  return lines.join('\n    ');
}

/** Keeps <head> in sync on client-side navigation. Renders nothing. */
export function Seo(props: SeoProps) {
  const collector = useContext(SeoContext);
  if (collector) collector.current = props;

  useEffect(() => {
    document.title = fullTitle(props.title);
    const head = document.head;

    let canonical = head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      head.appendChild(canonical);
    }
    canonical.href = absoluteUrl(props.path);

    head.querySelectorAll('meta[property^="article:"]').forEach((el) => el.remove());
    for (const t of metaTags(props)) {
      let el = head.querySelector<HTMLMetaElement>(`meta[${t.kind}="${t.key}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(t.kind, t.key);
        head.appendChild(el);
      }
      el.content = t.content;
    }

    head.querySelector('#page-jsonld')?.remove();
    if (props.jsonLd?.length) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'page-jsonld';
      script.textContent = jsonLdString(props.jsonLd);
      head.appendChild(script);
    }
  }, [props]);

  return null;
}

/** BreadcrumbList structured data from [name, path] pairs (Home is added automatically). */
export function breadcrumbLd(trail: Array<[name: string, path: string]>): JsonLd {
  const items: Array<[string, string]> = [['Home', '/'], ...trail];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(([name, path], i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: absoluteUrl(path),
    })),
  };
}
